import { Link } from "@tanstack/react-router";

import { RunStatusBadge } from "@/components/desk-suggestions/run-status-badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/date-utils";
import type { DeskSuggestionRun } from "@/lib/types/desk-suggestions";

interface RunsTableProps {
  runs: DeskSuggestionRun[];
  selectedRunId: string;
  onSelect: (runId: string) => void;
}

export function RunsTable({ runs, selectedRunId, onSelect }: RunsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Status</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Source import job</TableHead>
          <TableHead className="text-right">Desks</TableHead>
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => {
          const selected = run.id === selectedRunId;
          return (
            <TableRow
              key={run.id}
              data-state={selected ? "selected" : undefined}
              aria-current={selected ? "true" : undefined}
              className="cursor-pointer"
              onClick={() => {
                onSelect(run.id);
              }}
            >
              <TableCell>
                <RunStatusBadge status={run.status} />
              </TableCell>
              <TableCell>{formatDateTime(run.created_at)}</TableCell>
              <TableCell>
                {run.import_job_id ? (
                  <Link
                    to="/itsm-import/$jobId"
                    params={{ jobId: run.import_job_id }}
                    search={{ tenantId: run.tenant_id }}
                    className="font-mono text-xs underline-offset-4 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {run.import_job_id}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                {run.desks?.length ?? "—"}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant={selected ? "secondary" : "outline"}
                  aria-pressed={selected}
                  aria-label={`Review run from ${formatDateTime(run.created_at)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(run.id);
                  }}
                >
                  Review
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
